/**
 * Responsive Utility Tokens
 * Consistent responsive patterns for visibility, flex, and layout
 */

export const RESPONSIVE = {
  // Visibility helpers
  mobileOnly: "block sm:hidden",
  tabletUp: "hidden sm:block",
  desktopOnly: "hidden lg:block",
  mobileHidden: "hidden sm:block",
  tabletHidden: "block sm:hidden md:block",
  
  // Flex direction patterns
  stackToRow: "flex flex-col sm:flex-row",
  stackToRowMd: "flex flex-col md:flex-row",
  stackToRowMdCenter: "flex flex-col md:flex-row md:items-center md:justify-between gap-4",
  stackToRowLg: "flex flex-col lg:flex-row",
  
  // Gap patterns
  gapResponsive: "gap-3 sm:gap-4",
  gapResponsiveLg: "gap-4 sm:gap-6",
  
  // Spacing patterns
  paddingResponsive: "p-3 sm:p-4 md:p-6",
  paddingXResponsive: "px-3 sm:px-4 md:px-6",
  paddingYResponsive: "py-3 sm:py-4 md:py-6",
  
  // Text truncation
  truncateMobile: "truncate sm:whitespace-normal",
  
  // Width patterns
  fullToAuto: "w-full sm:w-auto",
  autoToFull: "w-auto sm:w-full",
  
  // Dialog/footer patterns (found in 70+ files)
  dialogFooter: "flex flex-col-reverse sm:flex-row gap-2",
  
  // Header with actions (found in 50+ files)
  headerWithActions: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4",
  headerWithActionsGap3: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3",
  headerWithActionsCompact: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2",
  
  // Filter row patterns
  filterRow: "flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4",
  
  // Table cell visibility
  cellHiddenMobile: "hidden sm:table-cell",
  cellHiddenTablet: "hidden md:table-cell",
  
  // Typography scaling
  textResponsive: "text-sm sm:text-base",
  textResponsiveLg: "text-base sm:text-lg",
  titleResponsive: "text-xl sm:text-2xl md:text-3xl",
  
  // List item patterns
  listItem: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3",
  listItemCompact: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2",
  
  // Inline visibility (for tab labels) - found in 40+ files
  tabletUpInline: "hidden sm:inline",
  
  // Header with start alignment (for card layouts with asymmetric content)
  headerWithActionsStart: "flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4",
  headerWithActionsStartGap3: "flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3",
  
  // Actions row patterns (simpler than filter row, no justify-between)
  actionsRow: "flex flex-col sm:flex-row sm:items-center gap-2",
  actionsRowGap3: "flex flex-col sm:flex-row sm:items-center gap-3",
  
  // User profile header layouts
  userHeaderLayout: "flex flex-col sm:flex-row sm:items-center gap-4 text-center sm:text-left",
  
  // Toggle/setting row with responsive gap (for settings panels)
  settingRowResponsive: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4",
  
  // Processing card layout with larger gap
  processingCardLayout: "flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6",
  
  // Banner/card layout with responsive gap (for notification banners, list items)
  bannerLayout: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4",
  
  // Content filters with simple gap (simpler than filterRow, no justify-between)
  contentFilters: "flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4",
  
  // List item with responsive gap (for interactive list rows)
  listItemResponsive: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg border",
  
  // Small responsive gap (for compact layouts)
  gapResponsiveSmall: "gap-1 sm:gap-2",
  
  // Stretch actions row (for button groups that stretch on mobile)
  actionsRowStretch: "flex flex-col sm:flex-row items-stretch sm:items-center gap-2",
  
  // Header with lg breakpoint (for complex layouts)
  headerWithActionsLg: "flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4",
  
  // Footer bottom bar layout (md breakpoint)
  footerBottomBar: "flex flex-col md:flex-row md:justify-between md:items-center gap-4",
  
  // Card header with start alignment and responsive gap
  headerWithActionsStartResponsive: "flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4",
  
  // Responsive gap patterns
  gapResponsiveWide: "gap-2 sm:gap-4",
  
  // Card content with start alignment and responsive gap (for opportunity cards, list items)
  cardContentStart: "flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4",
  
  // Dialog footer with justify-end (for dialogs with right-aligned buttons)
  dialogFooterEnd: "flex flex-col sm:flex-row gap-2 sm:justify-end",
  
  // Inline metadata row (for wrapped metadata items)
  metadataRow: "flex flex-wrap items-center gap-2 sm:gap-4",
  
  // Filter row with responsive width (for search + filter combos)
  filterRowAuto: "flex flex-col sm:flex-row gap-2 w-full sm:w-auto",
  
  // Alert dialog footer (extends dialogFooter with gap-2)
  alertDialogFooter: "flex-col-reverse sm:flex-row gap-2",
  
  // Centered auth CTA layout (for login prompts with centered buttons)
  authCtaCentered: "flex flex-col sm:flex-row justify-center gap-2 sm:gap-3",
  
  // Filter row with wrap (for filter sections that wrap on mobile)
  filterRowWrap: "flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3",
  
  // Profile header layout (for expert profiles with image + name)
  profileHeaderLayout: "flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6",
  
  // Name row with badge (for names with verification badges)
  nameRowWithBadge: "flex flex-col sm:flex-row items-center sm:items-center gap-2",
  
  // Processing row actions (for action rows in processing cards)
  processingRowActions: "flex flex-col sm:flex-row sm:items-center gap-2",
  
  // Visibility: show as flex on tablet+
  tabletUpFlex: "hidden sm:flex",
  
  // Alert row with start alignment (for notification/alert cards)
  alertRowStart: "flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3",
  
  // Profile info row (for profile cards with avatar + info)
  profileInfoRow: "flex items-start gap-3 sm:gap-4 flex-1",
  
  // Space-Y responsive (for content sections)
  spaceYResponsive: "space-y-3 sm:space-y-4",

  // Dialog footer with gap-3 variant
  dialogFooterEndGap3: "flex flex-col-reverse sm:flex-row gap-3 sm:justify-end",
  
  // Typography scaling - small
  textResponsiveXs: "text-xs sm:text-sm",
  
  // Typography scaling - large
  textResponsiveLgXl: "text-lg sm:text-xl",
  
  // Page container padding
  pageContainerPy: "py-8 sm:py-12 lg:py-16",
  
  // Section vertical padding
  sectionPy: "py-10 sm:py-16",
  
  // Responsive margins
  marginBottomResponsive: "mb-4 sm:mb-6",
  marginBottomLarge: "mb-6 sm:mb-8",
  
  // Extra-small gap
  gapResponsiveXs: "gap-2 sm:gap-3",
  
  // Space-Y variants
  spaceYResponsiveLarge: "space-y-4 sm:space-y-6",
  spaceYResponsiveSmall: "space-y-1 sm:space-y-2",
  spaceYResponsiveXl: "space-y-6 sm:space-y-8",
  
  // Page/section title patterns
  titlePage: "text-2xl sm:text-3xl md:text-4xl font-bold",
  titleSection: "text-2xl sm:text-3xl md:text-4xl font-bold text-center",
  subtitleSection: "text-sm sm:text-lg text-muted-foreground text-center",
  
  // Additional margin patterns
  marginBottomSm: "mb-3 sm:mb-4",
  marginBottomXl: "mb-8 sm:mb-12",
  
  // Card content padding
  paddingCardContent: "p-3 sm:p-4",
  paddingCardContentWide: "p-4 sm:p-6",
  
  // Additional gap variants
  gapResponsiveMdLg: "gap-3 sm:gap-6",
  gapResponsiveXl: "gap-4 sm:gap-8",
  
  // Medium title pattern
  titleMd: "text-xl sm:text-2xl font-bold",
  
  // Compact container padding
  containerPyCompact: "py-8 sm:py-16",
  
  // Text responsive 2xl (without bold)
  textResponsive2xl: "text-xl sm:text-2xl",
  
  // Extra-extra-small text
  textResponsiveXxs: "text-[10px] sm:text-xs",
  
  // Text alignment responsive
  textAlignCenterToLeft: "text-center sm:text-left",
  textAlignRightToLeft: "text-right sm:text-left",
  
  // Metadata row with wide gap
  metadataRowWide: "flex flex-wrap items-center gap-4 sm:gap-8",
  
  // Large-to-XL gap (Footer grids)
  gapResponsiveLgXl: "gap-6 sm:gap-8",
  
  // Large title pattern (ExpertProfile, UserRegister)
  titleLg: "text-2xl sm:text-3xl",
  
  // Large input title style (PublicationContent, PageEditor)
  titleInputLg: "text-3xl sm:text-4xl font-bold",
  
  // Gap that collapses on larger screens (ProgressDisplay)
  gapCollapseToZero: "gap-3 sm:gap-0",
  
  // Section separator with border spacing (CommentsSection)
  sectionSeparator: "pt-6 sm:pt-8 mt-6 sm:mt-8",
  
  // Opportunity card specific padding
  paddingCardOpportunity: "py-3 sm:py-4 px-3 sm:px-6",
  
  // Medium title with semibold (Phase 34)
  titleMdSemibold: "text-xl sm:text-2xl font-semibold",
  
  // Phase 35: Top margin patterns
  marginTopSm: "mt-2 sm:mt-3",
  marginTopXl: "mt-8 sm:mt-12",
  
  // Phase 37: Banner and breadcrumb patterns
  paddingBanner: "p-2 sm:p-3",
  pyBreadcrumb: "py-2 sm:py-3",
  paddingXResponsiveSm: "px-3 sm:px-4",
  gapResponsive1to2: "gap-1.5 sm:gap-2",
  
  // Phase 38: Typography patterns
  textResponsiveSmLgBold: "text-sm sm:text-lg font-bold",
  
  // Phase 39: Empty state padding
  pyEmptyState: "py-8 sm:py-12",
  
  // Phase 41: Field row gap pattern
  gapResponsive1to4: "gap-1 sm:gap-4",
  
  // Phase 16 Wave 7: Simple flex row patterns (non-responsive)
  flexRowBetween: "flex items-center justify-between",
  flexRowBetweenGap2: "flex items-center justify-between gap-2",
  flexRowBetweenGap3: "flex items-center justify-between gap-3",
  flexRowBetweenGap4: "flex items-center justify-between gap-4",
  flexRowCenter: "flex items-center",
  flexRowCenterGap2: "flex items-center gap-2",
  flexRowCenterGap3: "flex items-center gap-3",
  flexRowCenterGap4: "flex items-center gap-4",
} as const;

export type ResponsiveToken = keyof typeof RESPONSIVE;
